import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

export interface UseHeaderStylesProps {
  actionWidth: CSSProperties['gridTemplateColumns'];
}

export const useHeaderStyles = makeStyles((theme: Theme) => ({
  gridContainer: {
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: ({ actionWidth }: UseHeaderStylesProps) =>
      `${String(actionWidth)} minmax(0, auto) ${String(actionWidth)}`,
    gridTemplateRows: 'minmax(24px, 1fr)',
    gridTemplateAreas: '"icon information action"',
    columnGap: '1rem',
  },
  iconContainer: {
    gridArea: 'icon',
    justifySelf: 'start',
  },
  informationContainer: {
    gridArea: 'information',
    justifySelf: 'center',
    maxWidth: '100%',
  },
  actionContainer: {
    gridArea: 'action',
    justifySelf: 'end',
    minWidth: theme.spacing(3),
  },
  icon: {
    color: theme.palette.common.gray800,
  },
  iconButton: {
    margin: theme.spacing(-1.5),
  },
}));

export const useHeaderInformationStyles = makeStyles(theme => ({
  subtitle: {
    fontSize: theme.spacing(1.5),
  },
  title: {
    fontFamily: theme.typography.body2.fontFamily,
  },
}));

export const usePaginationStyles = makeStyles(theme => ({
  paginationContainer: {
    gap: theme.spacing(1),
    flexWrap: 'unset',
  },
  button: {
    '&:hover': {
      backgroundColor: theme.palette.common.gray100,
    },
    '&:active': {
      backgroundColor: theme.palette.common.gray200,
    },
  },
  previousButton: {
    padding: theme.spacing(0.25, 1.1875, 0.25, 0),
  },
  nextButton: {
    padding: theme.spacing(0.25, 0, 0.25, 1.1875),
  },
  iconOffset: {
    marginBottom: '-1px',
  },
}));

export const useHoverActionStyles = makeStyles(theme => ({
  actionContainer: {
    height: theme.spacing(2),
  },
  actionsContainer: {
    gap: theme.spacing(3),
  },
  iconVariant: {
    margin: theme.spacing(-1.5),
  },
  textVariant: {
    color: theme.palette.common.gray600,
    cursor: 'pointer',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1,
    minWidth: 'unset',
    padding: theme.spacing(0),
    paddingBottom: theme.spacing(0.5),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const useTelephoneCallStyles = makeStyles(theme => ({
  callButton: {
    width: 'auto',
    float: 'right',
  },
  callButtonIcon: {
    fontSize: theme.spacing(2),
    color: 'unset',
    marginRight: '10%',
  },
  dialogBox: {
    '& .MuiDialog-paper': {
      minWidth: '30%',
      minHeight: '20%',
      padding: '0.75rem',
      gap: '10px',
    },
  },
  dialogTitle: {
    textAlign: 'center',
  },
  dialogContent: {
    textAlign: 'center',
  },
  dialogFooter: {
    paddingBottom: '10px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    '& .MuiButtonBase-root': {
      marginRight: '0.25rem',
      marginLeft: '0.25rem',
    },
  },
  dialogContentSubtitle: {
    paddingTop: '20px',
    fontSize: 'small',
  },
  activeCall: {
    backgroundColor: theme.palette.common.success,
    '&:hover': {
      backgroundColor: theme.palette.common.successHover,
    },
  },
  dialogError: {
    fontSize: 'small',
    color: theme.palette.common.red400,
  },
  callProgressbar: {
    marginBottom: '1rem',
  },
}));

export const useInformationStyles = makeStyles(
  theme => ({
    headerContainer: {
      padding: theme.spacing(0, 0, 1),
    },
    headerLeftText: {
      color: theme.palette.common.gray800,
    },
    icon: {
      fontSize: theme.spacing(2),
      color: theme.palette.common.gray800,
      '& *': {
        fill: theme.palette.common.gray800,
      },
    },
    list: {
      padding: 0,
    },
    listItem: {
      gap: '1rem',
      padding: theme.spacing(0.5, 0),
      userSelect: 'text',
      MozUserSelect: 'text',
      msUserSelect: 'text',
      WebkitUserSelect: 'text',

      '&:hover': {
        cursor: 'unset',
        marginLeft: theme.spacing(-2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        width: `calc(100% + ${theme.spacing(4)}px)`,
      },

      '& p': {
        color: theme.palette.common.gray800,
      },
    },
    listItemIcon: {
      minWidth: 'unset',
    },
    name: {
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      lineHeight: '2rem',
    },
    nameContainer: {
      marginBottom: theme.spacing(2),
      overflow: 'hidden',
    },
    tooltip: {
      display: 'inline-grid',
    },
    referralContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexDirection: 'row',
      marginBottom: theme.spacing(2),
    },
    portalLink: {
      paddingLeft: '0px',
      '& .MuiButtonBase-root': {
        padding: theme.spacing(1, 0.5),
      },
      '& .MuiTypography-root': {
        marginLeft: theme.spacing(1),
      },
    },
    infoIcon: {
      height: theme.spacing(2),
      width: theme.spacing(2),
      padding: theme.spacing(0.25),
      marginTop: theme.spacing(0.25),
    },
    taskInstructionsContainer: {
      display: 'flex',
      padding: theme.spacing(1),
      backgroundColor: theme.palette.common.blue50,
      border: `1px solid ${theme.palette.common.blue600}`,
      borderRadius: theme.misc.borderRadius,
      marginTop: '10px',
    },
    taskInstructionsText: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: theme.spacing(1),
    },
    rejectionReasonCaption: {
      color: theme.palette.common.gray600,
    },
    taskInstructionsHeader: {
      textTransform: 'uppercase',
      color: theme.palette.common.gray600,
    },
    taskInstructionsContent: {
      color: theme.palette.common.gray800,
    },
  }),
  {
    index: -37,
  },
);
